import { Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { CustomInputComponent } from 'components/input/custom-input/custom-input.component';
import { EditableContentComponent, EditableContentDisplayComponent, EditableContentEditComponent } from 'components/input/editable-content/editable-content.component';
import { EditableContentDirective } from 'components/input/editable-content/editable-content.directive';
import { IconType } from 'components/layout/icon/icon.component';
import { LogEntryStatus } from 'constants/LogEntryStatus';
import { FormattedContentDirective } from 'directives/formatted-content.directive';
import { InputType } from 'enums/InputType';
import { LogEntryEventDataType } from 'enums/LogEntryEventDataType';
import { LogEntryType } from 'enums/LogEntryType';
import { CommunicationLogEntryModel } from 'models/generated/Communication.Api.Models.CommunicationLogEntry';
import { Layout } from 'modules/layout';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { UserPipe } from 'pipes/user.pipe';
import { Subscription } from 'rxjs';
import { CommunicationService } from 'services/communication.service';
import { PushService } from 'services/push.service';
import { UserService } from 'services/user.service';
import { TaskActionMailComponent } from './task-action-mail.component';
import { LogEntryEventModel } from 'models/generated/Communication.Api.Models.LogEntryAdditionalData.LogEntryEvent';
import { LogEntryMailAttachmentModel, LogEntryMailModel } from 'models/generated/Communication.Api.Models.LogEntryAdditionalData.LogEntryMail';
import { FileStorageService } from 'services/file-storage.service';
import { StorageContainer } from 'constants/StorageContainer';

@Component({
	selector: 'communication-log-entry',
	templateUrl: './communication-log.component.html',
	imports: [TaskActionMailComponent, Layout, FormattedContentDirective, EditableContentDirective, EditableContentComponent, EditableContentDisplayComponent, EditableContentEditComponent, CustomInputComponent, TranslatePipe, CustomDatePipe, UserPipe]
})
export class CommunicationLogEntryComponent implements OnDestroy {

	private _entry!: CommunicationLogEntryModel;
	@Input() set entry(value: CommunicationLogEntryModel) {
		this._entry = value;
		this.parseAdditionalData();
	}
	get entry() { return this._entry; }
	@ViewChild('textInput') textInput?: ElementRef<HTMLElement>;

	eventLogData?: LogEntryEventModel;
	emailData?: LogEntryMailModel;
	logEntryType = LogEntryType;
	busy = false;
	mailReplyOpen = false;
	inputType = InputType;
	logEntryStatus = LogEntryStatus;

	private updateSubscription: Subscription;

	constructor(
		private communicationLog: CommunicationLogComponent,
		private communicationService: CommunicationService,
		private pushService: PushService,
		private userService: UserService,
		private fileStorageService: FileStorageService,
		private customDate: CustomDatePipe,
		private translate: TranslatePipe) {

		this.updateSubscription = this.pushService.onCommunicationLogEntryChanged.subscribe(event => {
			if (this.entry && this.entry.id === event.data.logEntryId) {
				this.communicationService.getLogEntry(this.entry.id as string).then(entry => this.updateEntry(entry));
			}
		});
	}

	ngOnDestroy() {
		this.updateSubscription.unsubscribe();
	}

	getEntryIcon(entry: CommunicationLogEntryModel): IconType {
		switch (entry.type) {
			case LogEntryType.Mail:
				return 'envelope';
			case LogEntryType.Internal:
				return 'comment';
			case LogEntryType.Event:
				return 'list-check';
			default:
				return 'circle-question';
		}
	}

	isHtmlContent(): boolean {
		if (this.entry.type === LogEntryType.Mail) return true;
		if (this.entry.type === LogEntryType.Event && this.eventLogData?.dataType === LogEntryEventDataType.Text) return true;
		return false;
	}

	isEditable(): boolean {
		if (this.entry.type === LogEntryType.Mail) return false;
		if (this.entry.type === LogEntryType.Event) return false;
		if (this.entry.userId) return this.entry.userId === this.userService.getCurrentUser().id;
		return false;
	}

	getColorCode(status?: string): string {
		let result = 'status-color-indifferent';
		switch (status) {
			case undefined:
			case LogEntryStatus.none:
			case LogEntryStatus.noResponse:
				result = 'status-color-indifferent';
				break;
			case LogEntryStatus.meeting:
			case LogEntryStatus.rescheduledMeeting:
			case LogEntryStatus.createSale:
				result = 'status-color-positive';
				break;
			case LogEntryStatus.dialog:
			case LogEntryStatus.mailSent:
			case LogEntryStatus.mailReceived:
				result = 'status-color-neutral';
				break;
			case LogEntryStatus.rejected:
			case LogEntryStatus.offerRejected:
			case LogEntryStatus.meetingCancelled:
				result = 'status-color-negative';
				break;
			case LogEntryStatus.event:
				result = 'status-color-system';
				break;
		}

		return result;
	}

	async updateComment(comment: string) {
		this.busy = true;
		const oldComment = this.entry.comment;
		this.entry.comment = comment;
		try {
			this.updateEntry(await this.communicationService.updateCommunicationLogEntry(this.entry.id!, comment));
		}
		catch (error) {
			this.entry.comment = oldComment;
		}
		this.busy = false;
	}

	// Update properties of existing model to avoid breaking binding to parent models
	updateEntry(updatedEntry: CommunicationLogEntryModel) {
		this.entry.comment = updatedEntry.comment;
		this.entry.edits = updatedEntry.edits;
		this.entry.modified = updatedEntry.modified;
		this.entry.userId = updatedEntry.userId;
	}

	parseAdditionalData() {
		if (!this.entry.additionalData) return;
		switch (this.entry.type) {
			case LogEntryType.Event:
				this.eventLogData = JSON.parse(this.entry.additionalData);
				break;
			case LogEntryType.Mail:
				this.emailData = JSON.parse(this.entry.additionalData);
				break;
		}
	}

	getEventValue(type: LogEntryEventDataType, value: string) {
		switch (type) {
			case LogEntryEventDataType.Date:
				return this.customDate.transform(value, 'shortDate');
			case LogEntryEventDataType.DateTime:
				return this.customDate.transform(value);
			case LogEntryEventDataType.Area:
				return this.translate.transform('task-area.area-name.' + value);
			case LogEntryEventDataType.User:
				return this.userService.user(value)?.name;
			default:
				return value;
		}
	}

	async sendMail(mail: { comment: string, subject: string, recipients: string[]; }) {
		if (!this.communicationLog.sendMailFunction) return;
		
		await this.communicationLog.sendMailFunction(mail);
		this.mailReplyOpen = false;
	}

	downloadAttachment(attachment: LogEntryMailAttachmentModel) {
		this.fileStorageService.downloadFile(attachment.filePath, StorageContainer.mailAttachments);
	}
}

@Component({
	selector: 'communication-log',
	template: '@for (entry of communicationLog; track entry; let last = $last) {' +
		'<communication-log-entry [entry]="entry"></communication-log-entry>' +
		'@if (!last) { <hr /> }' +
		'}',
	imports: [CommunicationLogEntryComponent]
})
export class CommunicationLogComponent {

	@Input() communicationLog: CommunicationLogEntryModel[] = [];
	@Input() sendMailFunction?: (mail: { comment: string, subject: string, recipients: string[]; }) => Promise<void>;
}
